/**
 * Fills the Events and Learn desktop menu panels with live content from aadhyawellness.com
 * (Framer CMS, served by the Supabase function shop-cms-feed). Fetched once per visit, the
 * first time a panel is about to open (or when the browser is idle), and kept for 10 minutes.
 */
const FEED_URL = 'https://grgxirkxpywyboreqybw.supabase.co/functions/v1/shop-cms-feed';
const STORE_KEY = 'aadhya-cms-feed-v1';
const MAX_AGE = 10 * 60 * 1000;

let feedPromise;

function loadFeed() {
  if (feedPromise) return feedPromise;
  try {
    const cached = JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null');
    if (cached && Date.now() - cached.at < MAX_AGE) {
      feedPromise = Promise.resolve(cached.data);
      return feedPromise;
    }
  } catch (e) {}
  const timeout = new Promise((resolve) => setTimeout(() => resolve({ events: [], articles: [], timeout: true }), 6000));
  feedPromise = Promise.race([
    fetch(FEED_URL, { credentials: 'omit' })
      .then((r) => (r.ok ? r.json() : { events: [], articles: [] }))
      .catch(() => ({ events: [], articles: [] })),
    timeout,
  ])
    .then((data) => {
      if (!data.timeout) {
        try {
          sessionStorage.setItem(STORE_KEY, JSON.stringify({ at: Date.now(), data }));
        } catch (e) {}
      }
      return data;
    });
  return feedPromise;
}

const esc = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

const safeUrl = (u) => (/^https:\/\//.test(u || '') ? u : '');
const sized = (u) => (u.includes('framerusercontent.com') ? u + (u.includes('?') ? '&' : '?') + 'scale-down-to=800' : u);

function formatDate(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Europe/London' });
}

function eventCard(e) {
  const img = safeUrl(e.image);
  return `
    <a class="aadhya-card aadhya-card--event" href="${esc(safeUrl(e.url))}">
      <span class="aadhya-card__media">${img ? `<img src="${esc(sized(img))}" alt="" loading="lazy">` : ''}
        ${e.status === 'Live' ? '<span class="aadhya-card__live">Live now</span>' : ''}
      </span>
      <span class="aadhya-card__body">
        <span class="aadhya-card__chips">
          ${e.date ? `<span class="aadhya-chip aadhya-chip--date">${esc(formatDate(e.date))}</span>` : ''}
          ${e.location ? `<span class="aadhya-chip">${esc(e.location === 'Offline' ? 'In person' : e.location)}</span>` : ''}
        </span>
        <span class="aadhya-card__title">${esc(e.title)}</span>
        ${e.timeLabel ? `<span class="aadhya-card__meta">${esc(e.timeLabel)}</span>` : ''}
        ${e.summary ? `<span class="aadhya-card__text">${esc(e.summary)}</span>` : ''}
        <span class="aadhya-card__cta">View &amp; reserve <span aria-hidden="true">&rarr;</span></span>
      </span>
    </a>`;
}

function articleCard(a) {
  const img = safeUrl(a.image);
  return `
    <a class="aadhya-card aadhya-card--article" href="${esc(safeUrl(a.url))}">
      <span class="aadhya-card__media">${img ? `<img src="${esc(sized(img))}" alt="" loading="lazy">` : ''}</span>
      <span class="aadhya-card__body">
        ${a.readTime ? `<span class="aadhya-card__chips"><span class="aadhya-chip">${esc(a.readTime)}</span></span>` : ''}
        <span class="aadhya-card__title">${esc(a.title)}</span>
        ${a.excerpt ? `<span class="aadhya-card__text">${esc(a.excerpt)}</span>` : ''}
        <span class="aadhya-card__cta">Read the guide <span aria-hidden="true">&rarr;</span></span>
      </span>
    </a>`;
}

function emptyCard(kind, url) {
  const text =
    kind === 'events'
      ? 'New sessions are announced regularly. See what is coming up on the events page.'
      : 'Explore our guides to crystals, energy and everyday rituals.';
  return `
    <a class="aadhya-card aadhya-card--empty" href="${esc(url)}">
      <span class="aadhya-card__title">${kind === 'events' ? 'More sessions coming soon' : 'Visit the Aadhya journal'}</span>
      <span class="aadhya-card__text">${text}</span>
      <span class="aadhya-card__cta">${kind === 'events' ? 'See events' : 'Read articles'} <span aria-hidden="true">&rarr;</span></span>
    </a>`;
}

class AadhyaCmsCards extends HTMLElement {
  connectedCallback() {
    const item = this.closest('.menu-list__list-item');
    const load = () => this.render();
    // start loading as soon as the visitor points at the menu item, or when the page is idle
    item?.addEventListener('pointerenter', load, { once: true });
    item?.addEventListener('focusin', load, { once: true });
    (window.requestIdleCallback || ((fn) => setTimeout(fn, 2500)))(load);
  }

  async render() {
    if (this.rendered) return;
    this.rendered = true;
    const kind = this.dataset.kind;
    const data = await loadFeed();
    const list = (kind === 'events' ? data.events : data.articles) || [];
    if (!list.length) {
      this.dataset.count = '1';
      this.innerHTML = emptyCard(kind, this.dataset.emptyUrl);
      return;
    }
    this.dataset.count = String(Math.min(list.length, 3));
    this.innerHTML = list.slice(0, 3).map(kind === 'events' ? eventCard : articleCard).join('');
  }
}

if (!customElements.get('aadhya-cms-cards')) customElements.define('aadhya-cms-cards', AadhyaCmsCards);
